'use client'

import { useState } from 'react'
import { cn } from '@/lib/utils/cn'
import { Database, BrainCircuit, MessageCircle } from 'lucide-react'

const features = [
  {
    id: 'connect',
    title: '1. Connect Your Data',
    description: 'Integrate with your POS, review platforms (Google, Yelp), and reservation systems in just a few clicks. We unify your fragmented data into one secure source of truth.',
    icon: Database,
    color: 'text-blue-500',
    bgColor: 'bg-blue-500/10',
  },
  {
    id: 'analyze',
    title: '2. AI Analysis',
    description: 'Our advanced AI models process your customer feedback and sales data in real-time. We identify trends, sentiment, and opportunities for growth that you might miss.',
    icon: BrainCircuit,
    color: 'text-purple-500',
    bgColor: 'bg-purple-500/10',
  },
  {
    id: 'automate',
    title: '3. Automate Action',
    description: 'Let Encore handle the busy work. Auto-draft responses to reviews, generate weekly performance reports, and trigger marketing campaigns based on customer behavior.',
    icon: MessageCircle,
    color: 'text-emerald-500',
    bgColor: 'bg-emerald-500/10',
  },
]

export function Features() {
  const [activeFeature, setActiveFeature] = useState(0)

  return (
    <section className="py-24 bg-encore-gray-50 dark:bg-encore-dark">
      <div className="container mx-auto px-4 md:px-6">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4 text-encore-gray-900 dark:text-white">
            How Encore Works
          </h2>
          <p className="text-lg text-encore-gray-600 dark:text-encore-gray-400 max-w-2xl mx-auto">
            A simple, powerful workflow designed to save you hours every week.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
          {/* Left Side: Interactive List */}
          <div className="space-y-6">
            {features.map((feature, index) => (
              <div
                key={feature.id}
                className={cn(
                  "group cursor-pointer rounded-xl p-6 transition-all duration-300 border",
                  activeFeature === index
                    ? "bg-white dark:bg-encore-gray-800 border-encore-primary/20 shadow-lg scale-[1.02]"
                    : "bg-transparent border-transparent hover:bg-white/50 dark:hover:bg-white/5"
                )}
                onClick={() => setActiveFeature(index)}
              >
                <div className="flex gap-4">
                  <div className={cn(
                    "h-12 w-12 rounded-lg flex items-center justify-center shrink-0 transition-colors",
                    activeFeature === index ? feature.bgColor : "bg-gray-100 dark:bg-gray-800"
                  )}>
                    <feature.icon className={cn(
                      "h-6 w-6",
                      activeFeature === index ? feature.color : "text-gray-400"
                    )} />
                  </div>
                  <div>
                    <h3 className={cn(
                      "text-xl font-bold mb-2 transition-colors",
                      activeFeature === index ? "text-encore-gray-900 dark:text-white" : "text-gray-500"
                    )}>
                      {feature.title}
                    </h3>
                    <p className={cn(
                      "text-sm leading-relaxed transition-colors",
                      activeFeature === index ? "text-encore-gray-600 dark:text-encore-gray-300" : "text-gray-400"
                    )}>
                      {feature.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Right Side: Dynamic Visual */}
          <div className="relative h-[500px] w-full bg-white dark:bg-encore-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-2xl overflow-hidden p-8 flex items-center justify-center">
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-[0.03]" 
              style={{ backgroundImage: 'radial-gradient(#000 1px, transparent 1px)', backgroundSize: '20px 20px' }} 
            />
            
            {/* Animated Content based on selection */}
            <div className="relative z-10 w-full max-w-sm">
              {activeFeature === 0 && (
                <div className="animate-fade-in space-y-4">
                  <div className="flex items-center gap-4 p-4 bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-100 dark:border-gray-700">
                    <div className="h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-bold">G</div>
                    <div>
                      <div className="font-bold">Google Reviews</div>
                      <div className="text-xs text-green-500 flex items-center gap-1">● Connected</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 p-4 bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-100 dark:border-gray-700 ml-8">
                    <div className="h-10 w-10 bg-red-100 rounded-full flex items-center justify-center text-red-600 font-bold">Y</div>
                    <div>
                      <div className="font-bold">Yelp</div>
                      <div className="text-xs text-green-500 flex items-center gap-1">● Connected</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 p-4 bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-100 dark:border-gray-700">
                    <div className="h-10 w-10 bg-orange-100 rounded-full flex items-center justify-center text-orange-600 font-bold">P</div>
                    <div>
                      <div className="font-bold">POS System</div>
                      <div className="text-xs text-green-500 flex items-center gap-1">● Syncing...</div>
                    </div>
                  </div>
                </div>
              )}

              {activeFeature === 1 && (
                <div className="animate-fade-in">
                  <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-100 dark:border-gray-700 p-6">
                    <div className="flex items-center justify-between mb-6">
                      <h4 className="font-bold">Sentiment Analysis</h4>
                      <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded-full">AI Active</span>
                    </div>
                    <div className="space-y-4">
                      <div>
                        <div className="flex justify-between text-sm mb-1">
                          <span>Positive</span>
                          <span className="font-bold">78%</span>
                        </div>
                        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div className="h-full bg-green-500 w-[78%]" />
                        </div>
                      </div>
                      <div>
                        <div className="flex justify-between text-sm mb-1">
                          <span>Neutral</span>
                          <span className="font-bold">12%</span>
                        </div>
                        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div className="h-full bg-gray-400 w-[12%]" />
                        </div>
                      </div>
                      <div>
                        <div className="flex justify-between text-sm mb-1">
                          <span>Negative</span>
                          <span className="font-bold">10%</span>
                        </div>
                        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div className="h-full bg-red-500 w-[10%]" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeFeature === 2 && (
                <div className="animate-fade-in">
                  <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-100 dark:border-gray-700 p-4">
                    <div className="border-b border-gray-100 dark:border-gray-700 pb-2 mb-2 flex items-center gap-2">
                      <div className="h-2 w-2 rounded-full bg-red-500" />
                      <div className="h-2 w-2 rounded-full bg-yellow-500" />
                      <div className="h-2 w-2 rounded-full bg-green-500" />
                    </div>
                    <div className="space-y-3">
                      <div className="flex gap-2">
                        <div className="h-6 w-6 rounded-full bg-gray-200" />
                        <div className="bg-gray-100 dark:bg-gray-700 rounded p-2 text-xs w-3/4">
                          Can you recommend a response?
                        </div>
                      </div>
                      <div className="flex gap-2 flex-row-reverse">
                        <div className="h-6 w-6 rounded-full bg-emerald-500" />
                        <div className="bg-emerald-500/10 text-emerald-800 dark:text-emerald-300 rounded p-2 text-xs w-3/4">
                          Drafting response based on positive sentiment... Done!
                        </div>
                      </div>
                      <div className="h-8 bg-emerald-500 text-white text-xs font-bold rounded flex items-center justify-center cursor-pointer hover:opacity-90">
                        Send Response
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

